import React, { useRef, useState, useEffect } from "react";
import queryString from "query-string";
import Tabs, { Tab } from "../../../static/Tabs/Tabs";
import Gallery from "../../gallery/src/Gallery";
import LatestVideos from "../../LatestVideo/src/LatestVideo";
import VideoFeed from "../../VideoFeed/src/VideoFeed";
import { Article } from "../../../dynamic/articleList/src/ArticleList";
import { useComponentSize } from "../../../helpers/hooks/useComponentSize";
import { resizeVideos, uniqueBy } from "../../../helpers";
import "./index.scss";
import { getArticles } from "../../../services/news";
import { EventEmitter } from "fbemitter";
const emitter = new EventEmitter();

const lightboxListener = ({ callback }: { callback: Function }) => {
  emitter.addListener("galleryLB", ({ showLB }: { showLB: boolean }) => {
    callback({ showLB });
  });
};

export const lightboxEmitter = ({ showLB }: { showLB: boolean }) => {
  emitter.emit("galleryLB", { showLB });
};

interface sort {
  sort: number;
  tabName: string;
}

interface mediaTab {
  title: string;
  callback: Function;
  sort: number;
  flag: boolean | undefined;
  Component: React.ReactChild;
}
interface IArticle {
  id: number;
  title: string;
  summary: string;
  displayDate: string;
  imageUrl: null | string;
  imageUrls: [];
  articleUrl: string;
  category: string;
  type: string;
  tag: string[];
  circuitLogoLargeUrl: null | string;
  circuitLogoUrl: null | string;
  listingPageUr: string;
}

function LatestMediaTabs({
  articleLink,
  articleLinkText,
  gallery,
  galleryLink = "#0",
  galleryLinkText = "",
  playlistId = "",
  seeAllLink = "/",
  imageGallerytitle,
  seeAllLinkText = "See all Videos",
  title,
  articles = [],
  sort,
  loadQueryCode,
  SpecialEvent = false
}: {
  articleLink?: string;
  articleLinkText?: string;
  gallery?: any;
  galleryLink?: string;
  galleryStyling?: any;
  playlistId?: string;
  seeAllLink?: string;
  seeAllLinkText?: string;
  imageGallerytitle?: string;
  galleryLinkText?: string;
  title?: string;
  articles?: any[];
  sort?: sort[];
  loadQueryCode?: string;
  SpecialEvent?: boolean;
}) {
  let [list, setList] = useState<IArticle[]>(uniqueBy(articles, "id"));

  const articleFlag = list !== null && list !== undefined && list.length > 0;
  const galleryFlag = gallery !== null && gallery !== undefined;
  const videoFlag = playlistId.length !== 0;
  let ref = useRef(null);
  const mediaTabsWidth = useComponentSize(ref);
  const sorting = (a: mediaTab, b: mediaTab) => a.sort - b.sort;

  const [showing, setShowing] = useState<
    "ARTICLES" | "IMAGES" | "VIDEOS" | string
  >(
    sort
      ? sort.sort((a, b) => a.sort - b.sort)[0].tabName.toUpperCase()
      : articleFlag
      ? "ARTICLES"
      : galleryFlag
      ? "IMAGES"
      : "VIDEOS"
  );

  const [lightboxState, setLightBoxState] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  let [searchQuery, setSearchQuery] = useState("");

  const handleChangeQuery = () => {
    setSearchQuery(window.location.search);
  };

  useEffect(() => {
    lightboxListener({
      callback: ({ showLB }: { showLB: boolean }) => {
        setLightBoxState(showLB);
      }
    });
  });

  useEffect(() => {
    setSearchQuery(window.location.search);
    window.addEventListener("mousemove", handleChangeQuery);
    if (location.hash.toLocaleLowerCase().indexOf("gallerylightbox") > -1) {
      setShowing("IMAGES");
      const index = mediaTabs
        .sort(sorting)
        .findIndex(tab => tab.title.toUpperCase() === "IMAGES");
      setActiveTab(index > -1 ? index : 0);
    }
    return () => window.removeEventListener("mousemove", handleChangeQuery);
  }, []);

  useEffect(() => {
    if (!articles.length) {
      setShowing("IMAGES");
    }
    if (!loadQueryCode) return;
    const params = queryString.parse(searchQuery);
    const param = params && params[loadQueryCode.toLowerCase()];
    if (param) {
      getArticles({ [loadQueryCode.toLowerCase()]: param }).then(
        ({ items }: { items: IArticle[] }) => {
          setList(items);
        }
      );
    }
  }, [searchQuery]);

  const mediaTabs = [
    {
      title: "Articles",
      callback: () => {
        setShowing("ARTICLES");
      },
      sort: sort ? sort.filter(item => item.tabName == "Articles")[0].sort : -1,
      flag: articleFlag,
      Component: (
        <>
          <div className="grid-container articles">
            {articleFlag ? (
              <div className="article-list articles">
                {list.map((article: any) => (
                  <Article {...article} key={article.id} />
                ))}
              </div>
            ) : null}
          </div>
          {articleLink ? (
            <div className="media-tabs__link-container">
              <a className="btn" href={articleLink}>
                {articleLinkText}
              </a>
            </div>
          ) : null}
        </>
      )
    },
    {
      title: "Images",
      callback: () => {
        setShowing("IMAGES");
      },
      sort: sort ? sort.filter(item => item.tabName == "Images")[0].sort : -1,
      flag: galleryFlag,
      Component: (
        <>
          <div className="media-tabs__gallery">
            {showing === "IMAGES" ? (
              <Gallery
                galleryWidth={mediaTabsWidth.width}
                gallery={gallery}
                galleryLink={galleryLink}
                imageGallerytitle={imageGallerytitle}
                galleryLinkText={galleryLinkText}
              />
            ) : null}
          </div>
        </>
      )
    },
    {
      title: "Videos",
      callback: () => {
        resizeVideos({ ElClassName: "video-resize" });
        setShowing("VIDEOS");
      },
      sort: sort ? sort.filter(item => item.tabName == "Videos")[0].sort : -1,
      flag: videoFlag,
      Component: (
        <>
          <LatestVideos playlistId={playlistId} maxResults={1}>
            <VideoFeed offset={1} playlistId={playlistId} />
          </LatestVideos>
          <div
            className="media-tabs__link-container section"
            style={{ justifyContent: "center" }}
          >
            <a className="btn" target="_blank" href={seeAllLink}>
              {seeAllLinkText}
            </a>
          </div>
        </>
      )
    }
  ];

  return (
    <div
      className={`media-tabs__container${
        SpecialEvent ? " special-event" : ""
      } ${lightboxState ? showing : ""}`}
      ref={ref}
    >
      <Tabs
        className={`media-tabs${SpecialEvent ? " special-event" : ""}`}
        title={title}
        activeTab={activeTab}
        key={`activeTab-${activeTab}`}
      >
        {mediaTabs
          .sort(sorting)
          .map(
            ({ callback, flag, title, Component }: mediaTab, key: number) => (
              <Tab
                key={key}
                callback={callback}
                displayCondition={flag}
                tabTitle={title}
              >
                {Component}
              </Tab>
            )
          )}
      </Tabs>
    </div>
  );
}

export default LatestMediaTabs;
